import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Upload,
  X,
  Info,
  RefreshCw,
  CheckCircle
} from "lucide-react";
import { SBER, API, pageVariants, tapAnimation } from "../utils/constants";

const DocumentUploadPage = ({ user, onBack, onSuccess }) => {
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [customDocName, setCustomDocName] = useState('');
  const [customFile, setCustomFile] = useState(null);

  const userId = user?.telegram_id || user?.user_id;

  useEffect(() => {
    // Fetch pending document requests
    const fetchRequests = async () => {
      if (!userId) return;
      try {
        const res = await axios.get(`${API}/user/notifications?telegram_id=${userId}`);
        const requests = res.data.notifications?.filter(n => n.type === 'DOCUMENT_REQUEST' && !n.is_read) || [];
        setDocumentRequests(requests);
      } catch (err) {
        console.error('Error fetching document requests:', err);
      }
    };
    fetchRequests();
  }, [userId]);

  const handleFileSelect = (docName, file) => {
    setFiles(prev => ({ ...prev, [docName]: file }));
  };

  const handleCustomFileSelect = (e) => {
    if (e.target.files[0]) {
      setCustomFile(e.target.files[0]);
    }
  };

  const addCustomDocument = () => {
    if (!customDocName.trim() || !customFile) {
      toast.error('Укажите название и выберите файл');
      return;
    }
    setFiles(prev => ({ ...prev, [customDocName.trim()]: customFile }));
    setCustomDocName('');
    setCustomFile(null);
  };

  const removeFile = (docName) => {
    setFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[docName];
      return newFiles;
    });
  };

  const handleUpload = async () => {
    if (Object.keys(files).length === 0) {
      toast.error('Выберите файлы для загрузки');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('telegram_id', userId);
    if (user?.user_id) {
      formData.append('user_id', user.user_id);
    }
    
    Object.entries(files).forEach(([docName, file]) => {
      formData.append('files', file);
      formData.append('document_names', docName);
    });

    try {
      await axios.post(`${API}/user/upload-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Документы успешно загружены!');
      setFiles({});
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Ошибка загрузки документов');
    }
    setUploading(false);
  };

  const requestedDocs = documentRequests.flatMap(r => r.documents || []);

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-24"
      style={{ background: SBER.bg }}
      data-testid="document-upload-page"
    >
      {/* Header */}
      <div 
        className="px-5 pt-6 pb-4 flex items-center gap-4"
        style={{ background: SBER.white }}
      >
        <motion.button whileTap={tapAnimation} onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-6 h-6" style={{ color: SBER.dark }} />
        </motion.button>
        <h1 className="text-xl font-bold" style={{ color: SBER.dark }}>Загрузка документов</h1>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Info */}
        <div className="p-4 rounded-2xl" style={{ background: '#DBEAFE' }}>
          <p className="text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Загрузите документы для верификации. Поддерживаются изображения и PDF файлы.
          </p>
        </div>

        {/* Requested Documents */}
        {requestedDocs.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold" style={{ color: SBER.dark }}>Запрошенные документы:</h3>
            {requestedDocs.map((doc, idx) => (
              <div 
                key={idx} 
                className="p-4 rounded-2xl"
                style={{ background: SBER.white }}
                data-testid={`requested-doc-${idx}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-sm" style={{ color: SBER.dark }}>{doc}</span>
                  {files[doc] && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <label className="block">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileSelect(doc, e.target.files[0])}
                    className="hidden"
                    data-testid={`file-input-${idx}`}
                  />
                  <motion.div
                    whileTap={tapAnimation}
                    className="w-full py-3 rounded-xl flex items-center justify-center gap-2 cursor-pointer"
                    style={{ 
                      background: files[doc] ? SBER.greenPale : SBER.grayPale,
                      color: files[doc] ? SBER.green : SBER.gray
                    }}
                  >
                    <Upload className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      {files[doc] ? files[doc].name : 'Выбрать файл'}
                    </span>
                  </motion.div>
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Custom Document Upload */}
        <div className="p-4 rounded-2xl" style={{ background: SBER.white }}>
          <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Добавить документ:</h3>
          <input
            type="text"
            value={customDocName}
            onChange={(e) => setCustomDocName(e.target.value)}
            placeholder="Название документа"
            className="w-full p-3 rounded-xl mb-3 border-0 text-sm"
            style={{ background: SBER.grayPale, color: SBER.dark }}
            data-testid="custom-doc-name"
          />
          <label className="block mb-3">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleCustomFileSelect}
              className="hidden"
              data-testid="custom-file-input"
            />
            <motion.div
              whileTap={tapAnimation}
              className="w-full py-3 rounded-xl flex items-center justify-center gap-2 cursor-pointer"
              style={{ 
                background: customFile ? SBER.greenPale : SBER.grayPale,
                color: customFile ? SBER.green : SBER.gray
              }}
            >
              <Upload className="w-5 h-5" />
              <span className="text-sm font-medium">
                {customFile ? customFile.name : 'Выбрать файл'}
              </span>
            </motion.div>
          </label>
          <motion.button
            whileTap={tapAnimation}
            onClick={addCustomDocument}
            className="w-full py-3 rounded-xl font-medium text-sm"
            style={{ 
              background: (customDocName && customFile) ? SBER.green : SBER.grayPale,
              color: (customDocName && customFile) ? '#fff' : SBER.gray
            }}
            disabled={!customDocName || !customFile}
            data-testid="add-custom-doc-btn"
          >
            + Добавить документ
          </motion.button>
        </div>

        {/* Selected Files List */}
        {Object.keys(files).length > 0 && (
          <div className="p-4 rounded-2xl" style={{ background: SBER.white }}>
            <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Выбранные файлы ({Object.keys(files).length}):</h3>
            <div className="space-y-2">
              {Object.entries(files).map(([docName, file], idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: SBER.greenPale }}
                  data-testid={`selected-file-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: SBER.dark }}>{docName}</p>
                    <p className="text-xs truncate" style={{ color: SBER.gray }}>{file.name}</p>
                  </div>
                  <motion.button
                    whileTap={tapAnimation}
                    onClick={() => removeFile(docName)}
                    className="ml-2 p-2 rounded-full"
                    style={{ background: '#FEE2E2' }}
                    data-testid={`remove-file-${idx}`}
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </motion.button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        {Object.keys(files).length > 0 && (
          <motion.button
            whileTap={tapAnimation}
            onClick={handleUpload}
            disabled={uploading}
            className="w-full py-4 rounded-2xl font-semibold text-white flex items-center justify-center gap-2"
            style={{ background: uploading ? SBER.gray : SBER.green }}
            data-testid="upload-documents-btn"
          >
            {uploading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Загрузка...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Загрузить ({Object.keys(files).length})
              </>
            )}
          </motion.button>
        )}

        {/* Empty state */}
        {requestedDocs.length === 0 && Object.keys(files).length === 0 && (
          <div className="text-center py-6">
            <p className="text-sm" style={{ color: SBER.gray }}>
              Нет запросов на документы. Вы можете добавить документы для верификации выше.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DocumentUploadPage;
